import React, { useEffect, useState } from 'react';
import axios from "../../lib/axios.js";
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card.jsx';
import { Button } from '../../components/ui/button.jsx';
import { Input } from '../../components/ui/input.jsx';
import { Label } from '../../components/ui/label.jsx';
import { Separator } from '../../components/ui/separator.jsx';
import { Alert } from '../../components/ui/alert.jsx';
import { useAuth, useSignIn } from '@clerk/clerk-react';
import { PasswordInput } from '@/components/ui/password-input.jsx';
import { PhoneNumberInput } from '@/components/ui/phone-number-input.jsx';
import { Activity, Building, Building2, ChevronsUpDown, Loader2, PanelLeft, Users } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from '@/components/ui/avatar.jsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table.jsx';

const Signup = () => {
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const { isSignedIn, isLoaded } = useAuth();
    const { signIn, setActive } = useSignIn(); // Clerk authentication

    const isAuthenticated = isSignedIn && isLoaded;

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        company_name: '',
        phone_number: '',
    });

    const handleChange = (e) => {
        e.preventDefault();
        setFormData({
            ...formData,
            [e.target.id]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post('/register', formData);

            const result = await signIn.create({
                identifier: formData.email,
                password: formData.password,
            });

            if (result.status === 'complete') {
                await setActive({ session: result.createdSessionId });
            }

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'registrationComplete',
                userType: 'new',
                method: 'email'
            });

            navigate('/');
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error?.map(({ message }) => message).join(' ') || err.response?.data?.message || 'Registration failed');
        }
    };


    useEffect(() => {
        if (isAuthenticated) {
            navigate('/'); // Change to the route you want to redirect to
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);


    if (!isLoaded) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-background">
                <div className="flex flex-col items-center space-y-4">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </div>
        );
    }

    return (
        <div className="relative min-h-full w-full">
        <div className="flex min-h-full w-full overflow-hidden">
            {/* Sidebar */}
            <aside className="w-64 bg-gray-200 p-4 flex flex-col space-y-4 min-h-full">
                <div className="flex aspect-square size-20 items-center justify-center rounded-lg text-sidebar-primary-foreground">
                        <img
                            src={`${process.env.PUBLIC_URL}/logo-with-text.png`}
                            alt="Logo"
                            width="195"
                            className="blur-sm"
                        />
                </div>

                {/* Middle Menu Items - Takes Up Remaining Space */}
                <div className="flex-1 space-y-4">
                    <div className='flex blur-sm'>
                        <Building2 className='mr-2'/> <span>Communities</span>
                    </div>
                    <div className='flex blur-sm'>
                        <Building className='mr-2'/> <span>Companies</span>
                    </div>
                    <div className='flex blur-sm'>
                        <Activity className='mr-2'/> <span>Health</span>
                    </div>
                    <div className='flex blur-sm'>
                        <Users className='mr-2'/> <span>Users</span>
                    </div>
                </div>

                {/* Pushed to Bottom */}
                <div className='flex items-center blur-sm'>
                <Avatar className="h-8 w-8 rounded-lg">
                <AvatarFallback className="rounded-lg mr-2">JB</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">Jason Borne</span>
                <span className="truncate text-xs">JasonBorne@yahoo.com</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 p-6 blur-sm">
                {/* Header */}
                <div className="flex items-center mb-6">
                    <PanelLeft className='mr-2'/>
                    Dashboard
                </div>

                  <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[35%]">Community Name</TableHead>
                                    <TableHead className="w-[15%]">Community Phone</TableHead>
                                    <TableHead className="w-[15%]">Flair Phone</TableHead>
                                    <TableHead className="w-[15%]">Status</TableHead>
                                    <TableHead className="w-[20%]">OCAR % (30 days)</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                <TableRow>
                                    <TableCell>Communities Galore</TableCell>
                                    <TableCell>(555) 555-5555</TableCell>
                                    <TableCell>(555) 555-5555</TableCell>
                                    <TableCell>
                                        <Badge variant="secondary" className="font-normal">
                                            Active
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        100%
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>The Peach</TableCell>
                                    <TableCell>(555) 555-5555</TableCell>
                                    <TableCell>(555) 555-5555</TableCell>
                                    <TableCell>
                                        <Badge variant="secondary" className="font-normal">
                                            Secret Shopper
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        30%
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Olive Garden Apartments</TableCell>
                                    <TableCell>(555) 555-5555</TableCell>
                                    <TableCell>(555) 555-5555</TableCell>
                                    <TableCell>
                                        <Badge variant="secondary" className="font-normal">
                                            Deactivated
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        80%
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Pizza Hut Is My Favorite</TableCell>
                                    <TableCell>(555) 222-2222</TableCell>
                                    <TableCell>(555) 585-5555</TableCell>
                                    <TableCell>
                                        <Badge variant="secondary" className="font-normal">
                                            Active
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        66%
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Life Is Like A Box Of Chocolate</TableCell>
                                    <TableCell>(555) 123-8943</TableCell>
                                    <TableCell>(555) 666-6666</TableCell>
                                    <TableCell>
                                        <Badge variant="secondary" className="font-normal">
                                            Active
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        72%
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Hotel Chelsea</TableCell>
                                    <TableCell>(555) 978-3734</TableCell>
                                    <TableCell>(555) 866-1111</TableCell>
                                    <TableCell>
                                        <Badge variant="secondary" className="font-normal">
                                            Active
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        43%
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Flair Apartments</TableCell>
                                    <TableCell>(555) 555-5555</TableCell>
                                    <TableCell>(555) 555-5555</TableCell>
                                    <TableCell>
                                        <Badge variant="secondary" className="font-normal">
                                            Secret Shopper
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        87%
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Random Property</TableCell>
                                    <TableCell>(555) 475-4847</TableCell>
                                    <TableCell>(555) 576-9585</TableCell>
                                    <TableCell>
                                        <Badge variant="secondary" className="font-normal">
                                            Active
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        84%
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Community</TableCell>
                                    <TableCell>(555) 484-4848</TableCell>
                                    <TableCell>(555) 777-7777</TableCell>
                                    <TableCell>
                                        <Badge variant="secondary" className="font-normal">
                                            Deactivated
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        100%
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Communities Galore</TableCell>
                                    <TableCell>(555) 475-5755</TableCell>
                                    <TableCell>(555) 123-4567</TableCell>
                                    <TableCell>
                                        <Badge variant="secondary" className="font-normal">
                                            Active
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        10%
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </div>
            </main>
        </div>

        <div className="absolute top-0 flex items-center justify-center bg-black bg-opacity-50 w-full h-full px-4">
            <Card className="w-full max-w-md p-6">
                <CardHeader>
                    <CardTitle>Sign Up</CardTitle>
                </CardHeader>
                <CardContent>
                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <span>{error}</span>
                        </Alert>
                    )}
                    <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <Label htmlFor="first_name">First Name</Label>
                            <Input
                                id="first_name"
                                type="text"
                                onChange={handleChange}
                                placeholder="Enter your first name"
                                required
                            />
                        </div>
                        <div className="mb-4">
                            <Label htmlFor="last_name">Last Name</Label>
                            <Input
                                id="last_name"
                                type="text"
                                onChange={handleChange}
                                placeholder="Enter your last name"
                                required
                            />
                        </div>
                        <div className="mb-4">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                onChange={handleChange}
                                placeholder="Enter your email"
                                required
                            />
                        </div>
                        <div className="mb-4">
                            <Label htmlFor="phone_number">Phone Number</Label>
                            <PhoneNumberInput
                                id="phone_number"
                                name="phone_number"
                                type="tel"
                                required
                                pattern="^(\D*\d\D*){10}$"
                                title="Please enter a valid 10-digit phone number."
                                value={formData.phone_number}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="mb-4">
                            <PasswordInput
                                required
                                pattern="^(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{8,}$"
                                title="Must have at least 8 characters, one uppercase letter, and one special character."
                                value={formData.password}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="mb-4">
                            <Label htmlFor="company_name">Company Name</Label>
                            <Input
                                id="company_name"
                                type="text"
                                onChange={handleChange}
                                placeholder="Enter your company name"
                                required
                            />
                        </div>
                        <Separator className="my-4" />
                        <Button type="submit" className="w-full">Sign Up</Button>
                    </form>
                    <p className='mt-2'>Already have an account? <Link className='text-blue-500' to="/login">Login</Link></p>
                </CardContent>
            </Card>
        </div>
        </div>
    );
};

export default Signup;
